import React, { useState } from "react";
import { DayPicker } from "react-day-picker";
import { cs } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export const toIsoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Custom date picker in the Apex dark/gold style (replaces native white browser calendar)
export const ApexDatePicker = ({
  value, // Date | undefined
  onSelect, // (date: Date) => void
  fromDate,
  toDate,
  disabled, // optional react-day-picker matcher(s) for unselectable dates
  placeholder = "Vyberte datum",
  testId,
  trigger, // optional custom trigger element
  align = "start",
  className = "",
}) => {
  const [open, setOpen] = useState(false);

  const label = value
    ? value.toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" })
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <button
            type="button"
            className={`inline-flex items-center gap-2.5 px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-sm transition-colors hover:border-apex-gold/60 focus:outline-none focus:border-apex-gold ${value ? "text-apex-cream" : "text-apex-cream/45"} ${className}`}
            data-testid={testId}
          >
            <CalendarDays className="w-4 h-4 text-apex-gold flex-shrink-0" />
            {label}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={8}
        className="w-auto p-3 rounded-2xl bg-apex-surface border border-apex-gold/30 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.8),0_0_30px_-12px_rgba(229,200,128,0.25)]"
        data-testid={testId ? `${testId}-popover` : "apex-datepicker-popover"}
      >
        <DayPicker
          mode="single"
          selected={value}
          onSelect={(d) => {
            if (d) {
              onSelect?.(d);
              setOpen(false);
            }
          }}
          locale={cs}
          weekStartsOn={1}
          fromDate={fromDate}
          toDate={toDate}
          disabled={disabled}
          defaultMonth={value || fromDate || new Date()}
          showOutsideDays
          classNames={{
            months: "flex flex-col",
            month: "space-y-3",
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "text-sm font-semibold text-apex-gold font-display tracking-wide capitalize",
            nav: "space-x-1 flex items-center",
            nav_button:
              "h-7 w-7 rounded-full border border-apex-line bg-transparent text-apex-cream/70 hover:text-apex-gold hover:border-apex-gold/60 transition-colors inline-flex items-center justify-center",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell: "text-apex-gold/70 w-9 font-medium text-[0.7rem] uppercase tracking-wider text-center",
            row: "flex w-full mt-1.5",
            cell: "relative p-0 text-center",
            day: "h-9 w-9 p-0 rounded-lg text-sm text-apex-cream font-normal hover:bg-apex-gold/15 hover:text-apex-gold transition-colors aria-selected:opacity-100",
            day_selected:
              "bg-apex-gold !text-apex-bg font-bold hover:bg-apex-gold hover:!text-apex-bg shadow-[0_6px_18px_-6px_rgba(229,200,128,0.7)]",
            day_today: "ring-1 ring-apex-gold/60 text-apex-gold",
            day_outside: "!text-apex-cream/25",
            day_disabled: "!text-apex-cream/20 hover:bg-transparent cursor-not-allowed",
            day_hidden: "invisible",
          }}
          components={{
            IconLeft: (props) => <ChevronLeft className="h-4 w-4" {...props} />,
            IconRight: (props) => <ChevronRight className="h-4 w-4" {...props} />,
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
